import { useState, useEffect, useCallback, useMemo, Fragment } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getFacetedUniqueValues,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnOrderState,
  type GroupingState,
  type ExpandedState,
  type Row,
  type Table,
} from "@tanstack/react-table"
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  GripVertical,
  Columns3,
  Search,
  X,
  ChevronRight,
  ChevronDown,
  Check,
  RotateCcw,
} from "lucide-react"
import { cn, formatCellValue, parseDateOnly } from "@/lib/utils"
import { computeGroupTotals, currencyFieldOf, dataLeavesOf, groupDisplayLabel, isAdditiveColumn } from "@/lib/group-totals"
import { DataGridPagination } from "./pagination"
import type { GridState } from "@/types/report"

interface DataGridProps<T> {
  data: T[]
  columns: ColumnDef<T, any>[]
  isLoading: boolean
  error: string | null
  globalFilter: string
  onGlobalFilterChange: (value: string) => void
  showFilters: boolean
  tableRef?: (table: Table<T>) => void
  defaultHiddenColumns?: Record<string, boolean>
  /**
   * Column holding the chain root. Supplying it turns on the "Roll-up engagements" toggle: while
   * that is on the grid groups by this column ahead of any user grouping, and every group gets a
   * totals row. Omit and the grid has no roll-up control and never totals.
   */
  rollUpColumnId?: string
  appliedViewState?: GridState | null
  onFilteredDataChange?: (data: T[]) => void
}

function getStatusStyles(status: string) {
  switch (status) {
    case "Active": return "bg-emerald-500 text-white hover:bg-emerald-500"
    case "Completed": return "bg-blue-500 text-white hover:bg-blue-500"
    case "PendingChange": return "bg-orange-500 text-white hover:bg-orange-500"
    case "Inactive": return "bg-slate-400 text-white hover:bg-slate-400"
    case "Terminated": return "bg-red-500 text-white hover:bg-red-500"
    default: return "bg-slate-400 text-white hover:bg-slate-400"
  }
}

// Custom filter that supports both single string and multi-select array values
const multiSelectFilter = (row: any, columnId: string, filterValue: any) => {
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
  const cellValue = row.getValue(columnId)
  if (Array.isArray(filterValue)) return filterValue.includes(cellValue)
  return cellValue === filterValue
}

const numericFilter = (row: any, columnId: string, filterValue: any) => {
  if (filterValue == null || filterValue === "") return true
  const cellValue = row.getValue(columnId) as number
  const target = Number(filterValue)
  if (isNaN(target)) return true
  return cellValue === target
}

type DateFilterOp = "eq" | "ne" | "lt" | "gt" | "lte" | "gte" | "between"
type DateFilterValue = { op: DateFilterOp; value: string; value2?: string }

const DATE_OPS: { op: DateFilterOp; label: string; glyph: string }[] = [
  { op: "eq", label: "Equals", glyph: "=" },
  { op: "ne", label: "Does not equal", glyph: "≠" },
  { op: "lt", label: "Less than", glyph: "<" },
  { op: "gt", label: "Greater than", glyph: ">" },
  { op: "lte", label: "Less than or equal", glyph: "≤" },
  { op: "gte", label: "Greater than or equal", glyph: "≥" },
  { op: "between", label: "Between", glyph: "↔" },
]

const isKnownDateOp = (v: any): v is DateFilterOp =>
  v === "eq" || v === "ne" || v === "lt" || v === "gt" || v === "lte" || v === "gte" || v === "between"

function coerceDateFilterValue(raw: unknown): DateFilterValue | null {
  if (raw == null || raw === "") return null
  if (typeof raw === "string") return { op: "eq", value: raw }
  if (typeof raw === "object" && raw !== null) {
    const r = raw as any
    if (!isKnownDateOp(r.op)) return null
    const value = typeof r.value === "string" ? r.value : ""
    const value2 = typeof r.value2 === "string" ? r.value2 : undefined
    return { op: r.op, value, value2 }
  }
  return null
}

const dateOperatorFilter = (row: any, columnId: string, filterValue: any) => {
  const f = coerceDateFilterValue(filterValue)
  if (!f) return true
  if (!f.value && !f.value2) return true
  const cell = parseDateOnly(row.getValue(columnId))
  if (!cell) return false
  const cellT = cell.getTime()
  const a = f.value ? parseDateOnly(f.value) : null
  const b = f.value2 ? parseDateOnly(f.value2) : null
  if (f.op === "between") {
    if (!a && !b) return true
    if (a && !b) return cellT >= a.getTime()
    if (!a && b) return cellT <= b.getTime()
    const lo = Math.min(a!.getTime(), b!.getTime())
    const hi = Math.max(a!.getTime(), b!.getTime())
    return cellT >= lo && cellT <= hi
  }
  if (!a) return true
  const aT = a.getTime()
  switch (f.op) {
    case "eq": return cellT === aT
    case "ne": return cellT !== aT
    case "lt": return cellT < aT
    case "gt": return cellT > aT
    case "lte": return cellT <= aT
    case "gte": return cellT >= aT
  }
}

function PlaceholderDateInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [focused, setFocused] = useState(false)
  const showAsDate = !!value || focused
  return (
    <Input
      type={showAsDate ? "date" : "text"}
      value={value}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 text-xs border-slate-300 bg-white rounded w-full"
    />
  )
}

function DateFilterInput({ column, table }: { column: any; table: any }) {
  const meta = (column.columnDef.meta as any) ?? {}
  const raw = column.getFilterValue()

  const pairedColumn = meta.dateRangePair ? table.getColumn(meta.dateRangePair) : null

  const current = coerceDateFilterValue(raw)
  const op: DateFilterOp = current?.op ?? "eq"
  const value = current?.value ?? ""
  const value2 = current?.value2 ?? ""
  const glyph = DATE_OPS.find((o) => o.op === op)?.glyph ?? "="
  const hasAnyValue = !!current

  const isEmpty = (v: DateFilterValue) =>
    !v.value && !(v.op === "between" && v.value2)

  const setOp = (nextOp: DateFilterOp) => {
    const next: DateFilterValue = { op: nextOp, value }
    if (nextOp === "between" && value2) next.value2 = value2
    column.setFilterValue(next)
  }

  const setValue = (v: string) => {
    const next: DateFilterValue = { op, value: v }
    if (op === "between" && value2) next.value2 = value2
    if (isEmpty(next)) {
      column.setFilterValue(undefined)
      return
    }
    column.setFilterValue(next)
  }

  const setValue2 = (v: string) => {
    const next: DateFilterValue = { op, value }
    if (v) next.value2 = v
    if (isEmpty(next)) {
      column.setFilterValue(undefined)
      return
    }
    column.setFilterValue(next)
  }

  const clearValue = () => {
    column.setFilterValue(undefined)
    // Convenience: also clear the paired column if present (e.g., Start/End Date on Finance/Hours)
    pairedColumn?.setFilterValue(undefined)
  }

  const operatorButton = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-7 px-1.5 text-xs border-slate-300 bg-white font-normal flex-shrink-0"
          title={DATE_OPS.find((o) => o.op === op)?.label ?? "Equals"}
        >
          {glyph}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[180px] p-1">
        {DATE_OPS.map((o) => (
          <button
            key={o.op}
            type="button"
            className={cn(
              "flex items-center gap-2 w-full px-2 py-1 text-xs rounded-sm hover:bg-slate-100 transition-colors",
              op === o.op && "bg-slate-100 font-medium"
            )}
            onClick={() => setOp(o.op)}
          >
            <span className="w-4 text-center text-slate-600">{o.glyph}</span>
            <span>{o.label}</span>
          </button>
        ))}
        <button
          type="button"
          className="flex items-center gap-2 w-full px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm mt-1 border-t border-slate-100"
          onClick={clearValue}
        >
          <RotateCcw className="w-3 h-3" />
          Reset
        </button>
      </PopoverContent>
    </Popover>
  )

  const clearButton = hasAnyValue ? (
    <button
      type="button"
      onClick={clearValue}
      className="p-0.5 text-slate-400 hover:text-slate-700 rounded-sm flex-shrink-0"
      title="Clear date"
    >
      <X className="w-3 h-3" />
    </button>
  ) : null

  if (op === "between") {
    return (
      <div className="flex items-start gap-1">
        {operatorButton}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <PlaceholderDateInput
            value={value}
            onChange={setValue}
            placeholder="Start"
          />
          <PlaceholderDateInput
            value={value2}
            onChange={setValue2}
            placeholder="End"
          />
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <div className="h-7" aria-hidden="true" />
          {clearButton ?? <div className="h-7" aria-hidden="true" />}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      {operatorButton}
      <Input
        type="date"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-7 text-xs border-slate-300 bg-white rounded flex-1 min-w-0"
      />
      {clearButton}
    </div>
  )
}

export function FilterInput({ column, table, expanded }: { column: any; table: any; expanded?: boolean }) {
  const meta = (column.columnDef.meta as any) ?? {}
  const filterValue = column.getFilterValue() ?? ""

  if (meta.type === "date") {
    return <DateFilterInput column={column} table={table} />
  }

  if (meta.type === "boolean") {
    return (
      <Select value={filterValue == null ? "__all__" : String(filterValue)} onValueChange={(v) => column.setFilterValue(v === "__all__" ? undefined : v === "true")}>
        <SelectTrigger className="h-7 text-xs border-slate-300 bg-white">
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__" className="text-xs">All</SelectItem>
          <SelectItem value="true" className="text-xs">Yes</SelectItem>
          <SelectItem value="false" className="text-xs">No</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  // Resolve filter options: hardcoded array, lookup category, or unique column values
  let resolvedOptions: { code: string; label: string }[] | null = null
  if (meta.filterOptions) {
    resolvedOptions = meta.filterOptions.map((opt: string) => ({ code: opt, label: opt }))
  } else if (meta.uniqueValues) {
    const faceted = column.getFacetedUniqueValues() as Map<any, number>
    resolvedOptions = Array.from(faceted.keys())
      .filter((v) => v != null && v !== "")
      .map((v) => ({ code: String(v), label: String(v) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  if (resolvedOptions && resolvedOptions.length > 0) {
    const selected: string[] = Array.isArray(filterValue) ? filterValue : filterValue ? [filterValue as string] : []

    const toggleValue = (code: string) => {
      const next = selected.includes(code) ? selected.filter((v) => v !== code) : [...selected, code]
      column.setFilterValue(next.length > 0 ? next : undefined)
    }

    const label = selected.length === 0
      ? "All"
      : selected.length === 1
        ? resolvedOptions.find((o) => o.code === selected[0])?.label ?? selected[0]
        : `${selected.length} selected`

    const optionsList = (
      <>
        {resolvedOptions.map((opt) => (
          <button
            key={opt.code}
            type="button"
            className="flex items-center gap-2 w-full px-2 py-1 text-xs rounded-sm hover:bg-slate-100 transition-colors"
            onClick={() => toggleValue(opt.code)}
          >
            <div className={cn(
              "flex items-center justify-center w-3.5 h-3.5 rounded border",
              selected.includes(opt.code) ? "bg-slate-700 border-slate-700 text-white" : "border-slate-300"
            )}>
              {selected.includes(opt.code) && <Check className="w-2.5 h-2.5" />}
            </div>
            {opt.label}
          </button>
        ))}
        {selected.length > 0 && (
          <button
            type="button"
            className="w-full px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm mt-1 border-t border-slate-100"
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear all
          </button>
        )}
      </>
    )

    if (expanded) {
      return (
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md p-1 bg-white">
          {optionsList}
        </div>
      )
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-7 w-full text-xs border-slate-300 bg-white justify-between font-normal px-2">
            <span className="truncate">{label}</span>
            <ChevronDown className="w-3 h-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[180px] p-1 max-h-48 overflow-y-auto">
          {optionsList}
        </PopoverContent>
      </Popover>
    )
  }

  if (meta.type === "currency" || meta.type === "percent") {
    return (
      <Input
        type="number"
        value={filterValue as string}
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
        placeholder="Filter..."
        className="h-7 text-xs border-slate-300 bg-white rounded text-right"
      />
    )
  }

  return (
    <Input
      value={filterValue as string}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
      placeholder="Search..."
      className="h-7 text-xs border-slate-300 bg-white rounded"
    />
  )
}

export function DataGrid<T>({
  data,
  columns,
  isLoading,
  error,
  globalFilter,
  onGlobalFilterChange,
  showFilters,
  tableRef,
  defaultHiddenColumns = {},
  rollUpColumnId,
  appliedViewState,
  onFilteredDataChange,
}: DataGridProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultHiddenColumns)
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([])
  const [grouping, setGrouping] = useState<GroupingState>([])
  const [rollUp, setRollUp] = useState(false)

  // Roll-up is a mode, not a grouping the user picks from a list, so it is kept out of `grouping`
  // and prepended here. That keeps the Group By chips showing only what the user chose, and keeps
  // roll-up the OUTER grouping so any secondary grouping subdivides a chain rather than splitting it.
  const rollUpActive = !!rollUpColumnId && rollUp
  const effectiveGrouping = useMemo<GroupingState>(
    () => (rollUpActive ? [rollUpColumnId!, ...grouping.filter((id) => id !== rollUpColumnId)] : grouping),
    [rollUpActive, rollUpColumnId, grouping],
  )
  // Groups start COLLAPSED, and this must stay {} rather than true. With autoResetExpanded: false
  // set below, a `true` here would now genuinely stick and expand every group on a page — the
  // unbounded render that the collapsed default, together with paginateExpandedRows: false, exists
  // to prevent on a grid with no virtualisation.
  //
  // It used to say `true` with an "auto-expand all groups" comment that never took effect, because
  // TanStack's _autoResetExpanded overwrote it from initialState.expanded ?? {}. Note the reset does
  // NOT run before first paint — its first invocation only registers a flag — so `true` survived the
  // initial render harmlessly (grouping is empty then) and was cleared on the first row-model
  // rebuild, which is when grouping is applied. If auto-expansion is ever wanted, set
  // initialState.expanded, since that is what resetExpanded() reads.
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null)

  // Apply custom filter functions based on column meta
  const columnsWithFilters = columns.map((col: any) => {
    const meta = col.meta ?? col.columnDef?.meta
    if (meta?.filterOptions || meta?.uniqueValues) {
      return { ...col, filterFn: multiSelectFilter }
    }
    if (meta?.type === "date") {
      return { ...col, filterFn: dateOperatorFilter }
    }
    if (meta?.type === "currency" || meta?.type === "percent" || meta?.type === "number" || meta?.type === "integer") {
      return { ...col, filterFn: numericFilter }
    }
    return col
  })

  const table = useReactTable({
    data,
    columns: columnsWithFilters,
    state: { sorting, columnFilters, columnVisibility, columnOrder: columnOrder.length > 0 ? columnOrder : undefined, globalFilter, grouping: effectiveGrouping, expanded },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: { pagination: { pageSize: 50 } },
    // Keep groups open across filtering. getGroupedRowModel is memoised on the FILTERED row model,
    // so every filter edit and every keystroke of the global search rebuilds it and fires
    // _autoResetExpanded, which would collapse every group the user had opened - taking their
    // totals rows with them, since a total only renders under the last member of an expanded group.
    // With 50 collapsed groups to a page and no expand-all control, reopening them is several
    // clicks each time.
    //
    // Safe to disable: group row ids are `${columnId}:${groupingValue}`, so they stay valid across
    // filter changes, and a key left over from a different grouping simply matches no row.
    autoResetExpanded: false,
    // Keep every group whole on one page. The default paginates the flat expanded list, so a group
    // can straddle a page boundary - which splits its members and leaves the group's totals row
    // either absent or sitting under a partial group while summing every member, giving figures that
    // cannot be reconciled with the rows above them. Pages can now exceed pageSize; that is the
    // trade, and it is the one that keeps a total matching what is on screen.
    paginateExpandedRows: false,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    enableGrouping: true,
    groupedColumnMode: false,
  })

  useEffect(() => { if (tableRef) tableRef(table as any) }, [table, tableRef])

  // Apply view state when loaded
  useEffect(() => {
    if (!appliedViewState) return
    setSorting(appliedViewState.sorting ?? [])
    setColumnFilters(appliedViewState.columnFilters ?? [])
    // Layer the view over the defaults rather than replacing them. A view saved before a column
    // existed carries no entry for it, and a bare assignment would drop its default and reveal it -
    // which for a grouping key like Roll-up means a column of raw "Name (1234)" strings appearing
    // mid-report. An explicit entry in the view still wins, so someone who deliberately unhid a
    // column keeps it unhidden.
    setColumnVisibility({ ...defaultHiddenColumns, ...(appliedViewState.columnVisibility ?? {}) })
    setColumnOrder(appliedViewState.columnOrder ?? [])
    // The table's grouping state is effectiveGrouping, so anything persisted - a saved view, or the
    // session state ReportPage writes on every grouping change - carries the roll-up column whenever
    // the toggle was on. Read the toggle back out of it and keep the user's own grouping free of it.
    // Without this the grid restores grouped by the chain root with the toggle reading off, which
    // silently removes every totals row and leaves a removable chip for a column that is deliberately
    // absent from the Group By menu. It also makes Reset and Clear view turn roll-up off, since both
    // push a state with grouping: [].
    const viewGrouping = appliedViewState.grouping ?? []
    setRollUp(!!rollUpColumnId && viewGrouping.includes(rollUpColumnId))
    setGrouping(viewGrouping.filter((id) => id !== rollUpColumnId))
    if (appliedViewState.columnSizing && Object.keys(appliedViewState.columnSizing).length > 0) {
      table.setColumnSizing(appliedViewState.columnSizing)
    } else {
      table.resetColumnSizing()
    }
    if (appliedViewState.pagination?.pageSize) {
      table.setPageSize(appliedViewState.pagination.pageSize)
    }
    if (appliedViewState.globalFilter != null) {
      onGlobalFilterChange(appliedViewState.globalFilter)
    }
  }, [appliedViewState])

  // Emit filtered data to parent for non-table views
  const filteredRows = table.getFilteredRowModel().rows
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredRows.map((r) => r.original))
    }
  }, [filteredRows, onFilteredDataChange])

  // Column drag handlers — live reorder on dragOver for smooth UX
  const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    setDraggedColumnId(columnId)
    e.dataTransfer.setData("text/plain", columnId)
    e.dataTransfer.effectAllowed = "move"
  }, [])

  const [dropTargetColumnId, setDropTargetColumnId] = useState<string | null>(null)

  const handleDragOverHeader = useCallback((e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (draggedColumnId && draggedColumnId !== targetColumnId) {
      setDropTargetColumnId(targetColumnId)
    }
  }, [draggedColumnId])

  const handleDropOnHeader = useCallback((e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault()
    if (!draggedColumnId || draggedColumnId === targetColumnId) return

    const allColumns = table.getAllLeafColumns().map(c => c.id)
    const currentOrder = columnOrder.length > 0 ? [...columnOrder] : [...allColumns]
    const fromIndex = currentOrder.indexOf(draggedColumnId)
    const toIndex = currentOrder.indexOf(targetColumnId)
    if (fromIndex === -1 || toIndex === -1) return

    currentOrder.splice(fromIndex, 1)
    currentOrder.splice(toIndex, 0, draggedColumnId)
    setColumnOrder(currentOrder)
    setDraggedColumnId(null)
    setDropTargetColumnId(null)
  }, [draggedColumnId, columnOrder, table])

  const handleDragEnd = useCallback(() => {
    setDraggedColumnId(null)
    setDropTargetColumnId(null)
  }, [])

  const removeGrouping = useCallback((columnId: string) => {
    setGrouping(prev => prev.filter(id => id !== columnId))
  }, [])

  // The sections whose totals belong after this row, innermost first. The rule is:
  //
  //   the chain always totals · any section you can see rows in totals · levels between do not
  //
  // With no secondary grouping the chain IS the section holding the rows, so this yields the single
  // totals row it always has. Add one grouping and you get a subtotal per slice plus the chain total
  // at the end — the chain figure is the point of rolling up, and losing it to a slice made the two
  // features mutually exclusive. Skipping intermediate levels caps the stack at two rows however
  // deep the grouping goes, rather than a tower of totals at every chain boundary.
  const sectionsToTotalAfter = (row: Row<T>, next: Row<T> | undefined): Row<T>[] => {
    if (!rollUpActive) return []

    // Anchored on the last row RENDERED inside a section, not its last data row. That is what puts
    // the chain total at the chain's own level, beside its slice headers, instead of buried after
    // the last row of whichever slice happens to be expanded - where finding it means expanding
    // slices and hunting for a row that sits just above the next chain's header. It is also what
    // makes the chain total appear at all when the slices are collapsed and no data row renders.
    const nextAncestorIds = new Set<string>()
    for (let a = next?.getParentRow(); a; a = a.getParentRow()) nextAncestorIds.add(a.id)

    const sections: Row<T>[] = []
    for (let section = row.getParentRow(); section; section = section.getParentRow()) {
      // More of this section is still to come
      if (nextAncestorIds.has(section.id)) continue

      const isChain = !section.getParentRow()
      const holdsRows = section.subRows.some((sub) => !sub.getIsGrouped())
      if (!isChain && !holdsRows) continue

      // A section of one needs no total; it would just restate the row above it
      const dataLeafCount = dataLeavesOf(section).length
      if (dataLeafCount < 2) continue

      // If a section already totalling at this same anchor covers the same rows, the chain total
      // would repeat it verbatim - so drop the outer one. Compared by data-leaf count against what
      // was actually pushed, rather than by inspecting the shape of the chain's only slice: every
      // section here is an ancestor of the same row and therefore nested, so an equal count means
      // the same rows, at any depth. Inspecting the slice instead kept missing cases - a slice
      // holding further sections rather than rows totals nothing itself, which is the ordinary
      // shape once a chain shares a client or a branch with all its change requests.
      // Nothing pushed - every slice collapsed - leaves the chain its total, which is correct: it
      // is then the only total there is.
      const innermostTotalled = sections[0]
      const innermostCoversSameRows =
        !!innermostTotalled && dataLeavesOf(innermostTotalled).length === dataLeafCount
      if (isChain && innermostCoversSameRows) continue

      sections.push(section)
    }
    return sections
  }

  const renderGroupTotals = (group: Row<T>) => {
    // Computed by the shared helper, which the Excel export also calls - the totals in the file and
    // the totals on the screen are then the same arithmetic by construction. By column id rather
    // than by visible cell, so a ratio can reference parts that are hidden or not totalled.
    const { values, currency, mixedCurrency } = computeGroupTotals(group, table.getAllLeafColumns())

    // Named after the column this section groups on, which needs no special case: the chain groups
    // on the Roll-up column, so a single-level roll-up still reads "Roll-up totals" exactly as
    // before. The label only differentiates once a slice subtotal and the chain total sit adjacent,
    // where two rows both claiming to be the roll-up total would be genuinely ambiguous. Column
    // header rather than the slice value, since values can be long, numeric or absent.
    const sectionHeader = group.groupingColumnId
      ? table.getColumn(group.groupingColumnId)?.columnDef.header
      : undefined
    const totalLabel = typeof sectionHeader === "string" ? `${sectionHeader} totals` : "Totals"

    const columns = table.getVisibleLeafColumns()

    // The label goes in the first column that has nothing of its own to show, not simply the first
    // column: columns are drag-reorderable and hideable, so a fixed index would silently swallow a
    // real total the moment an amount column ended up leftmost.
    const labelColumnIdx = columns.findIndex((c) => {
      const m = (c.columnDef.meta as any) ?? {}
      return !isAdditiveColumn(m) && !m.groupTotalRatio
    })

    return (
      <TableRow className="border-b-2 border-slate-300 bg-slate-100/80 hover:bg-slate-100/80">
        {columns.map((column, columnIdx) => {
          const meta = (column.columnDef.meta as any) ?? {}
          const isAdditive = isAdditiveColumn(meta)
          // Match the body's alignment exactly so the figures line up under their column
          const isNumeric = meta.type === "currency" || meta.type === "percent"

          const total = values.get(column.id)

          let content: React.ReactNode = null
          if (columnIdx === labelColumnIdx) {
            // No colour override — the label reads at the same weight and shade as the figures it
            // introduces. The member count is already on the section header above.
            content = totalLabel
          } else if (mixedCurrency.has(column.id)) {
            content = <span className="font-normal text-slate-500">Mixed currency</span>
          } else if (total != null) {
            content = formatCellValue(total, meta.type, isAdditive ? currency.get(column.id) : undefined)
          }

          return (
            <TableCell
              key={`total-${group.id}-${column.id}`}
              className={cn(
                // font-bold, not semibold: at text-xs the step from the font-medium already used on
                // the code column is too small to read as emphasis.
                "text-xs whitespace-nowrap font-bold text-slate-900 border-r border-slate-200 last:border-r-0",
                isNumeric && "text-right tabular-nums",
              )}
              style={{ width: column.getSize() }}
            >
              {content}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-sm font-medium text-red-600">{error}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Top bar: group-by panel left, search + column chooser right */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        {/* Left: group-by multiselect dropdown */}
        <div className="flex items-center gap-2 flex-1 mr-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2 text-xs text-slate-600">
                <Columns3 className="w-3.5 h-3.5" />
                Group By
                {grouping.length > 0 && (
                  <span className="bg-slate-700 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none">
                    {grouping.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
              {/* The roll-up column is driven by its own toggle, so it is not offered here */}
              {table.getAllLeafColumns().filter(col => col.getCanGroup() && col.id !== rollUpColumnId).map((column) => {
                const header = column.columnDef.header
                const isGrouped = grouping.includes(column.id)
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={isGrouped}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setGrouping(prev => [...prev, column.id])
                      } else {
                        setGrouping(prev => prev.filter(id => id !== column.id))
                      }
                    }}
                    className="text-xs"
                  >
                    {typeof header === "string" ? header : column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Active group chips */}
          {grouping.map(columnId => {
            const col = table.getColumn(columnId)
            const header = col?.columnDef.header
            return (
              <span key={columnId} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded-md">
                {typeof header === "string" ? header : columnId}
                <button
                  type="button"
                  onClick={() => removeGrouping(columnId)}
                  aria-label={`Remove ${typeof header === "string" ? header : columnId} grouping`}
                  className="hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )
          })}
        </div>

        {/* Right: roll-up toggle, search, column chooser */}
        <div className="flex items-center gap-2">
          {rollUpColumnId && (
            <>
              <div className="flex items-center gap-2">
                <Switch
                  id="report-rollup"
                  checked={rollUp}
                  onCheckedChange={setRollUp}
                  aria-label="Group related engagements together and total each group"
                />
                <label htmlFor="report-rollup" className="text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                  Roll-up engagements
                </label>
              </div>
              <div className="h-5 w-px bg-slate-300" />
            </>
          )}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search all columns..."
              value={globalFilter}
              onChange={(e) => onGlobalFilterChange(e.target.value)}
              className="pl-9 h-9 bg-white border-slate-300"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-700 hover:bg-slate-200" aria-label="Toggle column visibility">
                <Columns3 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
              {/* The roll-up column is an internal grouping key, not something to show as a column */}
              {table.getAllLeafColumns().filter(column => column.id !== rollUpColumnId).map((column) => {
                const header = column.columnDef.header
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => column.toggleVisibility(!!checked)}
                    className="text-xs"
                  >
                    {typeof header === "string" ? header : column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table with horizontal + vertical scroll */}
      <div className="flex-1 overflow-auto">
        <table className="caption-bottom text-sm" style={{ width: table.getCenterTotalSize(), tableLayout: "fixed" }}>
          <TableHeader className="sticky top-0 z-20">
            {table.getHeaderGroups().map((headerGroup) => (
              <Fragment key={headerGroup.id}>
                {/* Dark slate column headers */}
                <TableRow key={headerGroup.id} className="bg-slate-700 hover:bg-slate-700 border-none">
                  {headerGroup.headers.map((header) => {
                    const meta = (header.column.columnDef.meta as any) ?? {}
                    const isNumeric = meta.type === "currency" || meta.type === "percent"
                    const sorted = header.column.getIsSorted()
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "bg-slate-700 text-[11px] font-semibold uppercase tracking-wider text-white overflow-hidden text-ellipsis whitespace-nowrap",
                          "border-r border-slate-600 last:border-r-0 select-none h-10",
                          header.column.getCanSort() && "cursor-pointer hover:bg-slate-600 transition-colors",
                          isNumeric && "text-right",
                          draggedColumnId === header.id && "opacity-50",
                          dropTargetColumnId === header.id && "border-l-2 border-l-blue-400"
                        )}
                        style={{ width: header.getSize() }}
                        onDragOver={(e) => handleDragOverHeader(e, header.id)}
                        onDrop={(e) => handleDropOnHeader(e, header.id)}
                      >
                        <div
                          className={cn(
                            "flex items-center gap-1 pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
                            isNumeric && "justify-end"
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              header.column.toggleSorting()
                            }
                          }}
                          role={header.column.getCanSort() ? "button" : undefined}
                          tabIndex={header.column.getCanSort() ? 0 : -1}
                          draggable
                          onDragStart={(e) => handleDragStart(e, header.id)}
                          onDragEnd={handleDragEnd}
                        >
                          <GripVertical className="w-3 h-3 opacity-50 cursor-grab flex-shrink-0" />
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" && <ArrowUp className="w-3 h-3 flex-shrink-0" />}
                          {sorted === "desc" && <ArrowDown className="w-3 h-3 flex-shrink-0" />}
                          {!sorted && header.column.getCanSort() && <ArrowUpDown className="w-3 h-3 opacity-40 flex-shrink-0" />}
                        </div>
                        {/* Resize handle — wide hit area, thin visual line */}
                        <div
                          onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            header.getResizeHandler()(e)
                          }}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            "absolute top-0 right-0 w-3 h-full cursor-col-resize select-none touch-none z-10",
                            "after:absolute after:top-0 after:right-0 after:w-[2px] after:h-full after:transition-colors",
                            header.column.getIsResizing()
                              ? "after:bg-blue-400"
                              : "after:bg-transparent hover:after:bg-blue-400"
                          )}
                        />
                      </TableHead>
                    )
                  })}
                </TableRow>

                {/* Filter row — conditionally shown */}
                {showFilters && (
                  <TableRow key={`${headerGroup.id}-filters`} className="bg-white hover:bg-white border-b border-slate-200">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={`filter-${header.id}`} className="h-9 px-1.5 border-r border-slate-200 last:border-r-0 font-normal">
                        {header.column.getCanFilter() && <FilterInput column={header.column} table={table} />}
                      </TableHead>
                    ))}
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 15 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`} className={cn(i % 2 === 1 && "bg-slate-50", "hover:bg-slate-50")}>
                  {table.getVisibleFlatColumns().map((col) => (
                    <TableCell key={col.id}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="hover:bg-white">
                <TableCell colSpan={table.getVisibleFlatColumns().length} className="h-32 text-slate-500 text-sm">
                  No results found. Try adjusting your filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row, idx) => {
                const isGrouped = row.getIsGrouped()

                // Computed before the branch below, because when a chain's slices are collapsed the
                // last row rendered inside it is a slice HEADER, and that is what the chain's totals
                // row has to follow.
                const totalsRows = sectionsToTotalAfter(row, table.getRowModel().rows[idx + 1]).map(
                  (section) => <Fragment key={`totals-${section.id}`}>{renderGroupTotals(section)}</Fragment>,
                )

                if (isGrouped) {
                  // Shared with the Excel export, so a group cannot be named one thing on screen and
                  // another in the file.
                  const groupedHeader = table.getColumn(row.groupingColumnId ?? "")?.columnDef.header
                  return (
                    <Fragment key={row.id}>
                    <TableRow className="bg-slate-100 hover:bg-slate-200 border-b border-slate-200 font-medium">
                      <TableCell colSpan={table.getVisibleFlatColumns().length} className="py-2">
                        <button
                          onClick={row.getToggleExpandedHandler()}
                          aria-expanded={row.getIsExpanded()}
                          className="flex items-center gap-1 text-sm text-slate-700"
                        >
                          {row.getIsExpanded() ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-semibold">{groupDisplayLabel(row.groupingValue, groupedHeader)}</span>
                          <span className="text-slate-500 font-normal ml-1">({row.subRows.length})</span>
                        </button>
                      </TableCell>
                    </TableRow>
                    {totalsRows}
                    </Fragment>
                  )
                }

                return (
                  <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      "border-b border-slate-100 transition-colors",
                      idx % 2 === 1 ? "bg-slate-50/50 hover:bg-blue-50" : "bg-white hover:bg-blue-50",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = (cell.column.columnDef.meta as any) ?? {}
                      const value = cell.getValue()
                      const isNumeric = meta.type === "currency" || meta.type === "percent"
                      const isNegative = meta.type === "currency" && typeof value === "number" && value < 0
                      const isPositive = (cell.column.id === "grossProfit" || cell.column.id === "unbilledRevenue") && typeof value === "number" && value > 0
                      const isStatus = meta.type === "status"
                      // By the column's own currency field, so a pay-side figure is not labelled
                      // with the bill currency on a report that carries both.
                      const rowCurrency = (row.original as any)?.[currencyFieldOf(meta)]
                      const formatted = formatCellValue(value, meta.type, rowCurrency)

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            "text-xs overflow-hidden text-ellipsis whitespace-nowrap text-slate-700 border-r border-slate-100 last:border-r-0",
                            isNumeric && "text-right tabular-nums",
                            isNegative && "text-red-600 font-medium",
                            isPositive && "text-emerald-600 font-medium",
                          )}
                          style={{ width: cell.column.getSize() }}
                        >
                          {isStatus ? (
                            <Badge className={cn("text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5", getStatusStyles(value as string))}>
                              {value === "PendingChange" ? "Pending" : formatted}
                            </Badge>
                          ) : (
                            <span className={cn(cell.column.id === "engagementCode" && "font-medium")}>
                              {formatted}
                            </span>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>

                  {totalsRows}
                  </Fragment>
                )
              })
            )}
          </TableBody>
        </table>
      </div>

      <DataGridPagination table={table} totalRows={data.length} />
    </div>
  )
}
